/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Line } from 'react-chartjs-2';
import moment from 'moment';

// Externals 📦
import {
  Portlet,
  PortletHeader,
  PortletLabel,
  PortletContent,
  PortletToolbar,
} from 'components';
import { Box } from '@mui/material';
import { Download, ShareButton } from 'components/Download';

/**
 * 📊 Componente GeneralReport
 *
 * Este componente muestra un gráfico de líneas 📈 con datos de temperatura 🌡️ y demanda eléctrica ⚡.
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.weather - Datos del clima (temperatura máxima y mínima por día) ☀️❄️
 * @param {number} props.updateYear - Año seleccionado para actualizar los datos 📅
 * @param {number} props.updateMonth - Mes seleccionado para actualizar los datos 📆
 * @param {string} props.title - Título del gráfico 🏷️
 * @param {string} props.legend - Leyenda para el conjunto de datos de demanda 📌
 * @param {string} props.maxWeather - Etiqueta para la temperatura máxima 🔥
 * @param {string} props.minWeather - Etiqueta para la temperatura mínima ❄️
 * @param {Object} props.generalReport - Reporte general con datos de demanda eléctrica ⚙️
 */
export default function GeneralReport({
  weather,
  updateYear,
  updateMonth,
  title,
  legend,
  maxWeather,
  minWeather,
  generalReport,
}) {
  const [weatherData, setWeatherData] = useState({});
  const [maxTemp, setMaxTemp] = useState([]);
  const [minTemp, setMinTemp] = useState([]);
  const [days, setDays] = useState([]);
  const chartRef = useRef();
  const localeNav = useSelector((state) => state.prefs.locale);

  const year = new Date().getFullYear();
  const month = new Date().getMonth() + 1;
  const daysInMonth = moment().daysInMonth();

  // 📅 Genera los días del mes seleccionado
  useEffect(() => {
    const currentMonth = moment().month();
    const currentYear = moment().year();
    const daysArray = [];
    moment.locale(localeNav);
    const daysInMonth = updateYear
      ? moment(`${updateYear}${updateMonth}`).daysInMonth()
      : moment().daysInMonth();

    for (let i = 1; i <= daysInMonth; i++) {
      const date = moment({
        year: updateYear || currentYear,
        month: updateMonth ? updateMonth - 1 : currentMonth,
        day: i,
      });
      daysArray.push({
        day: i,
        weekday: date.format('ddd'),
      });
    }
    setDays(daysArray);
  }, [localeNav, updateMonth, updateYear]);

  // 🌦️ Procesa los datos del clima y rellena días sin datos con valores por defecto
  useEffect(() => {
    const newWeatherData = {};
    Object.keys(weather).forEach((dateKey) => {
      newWeatherData[dateKey] = weather[dateKey];
    });

    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
      if (!newWeatherData[dateKey]) {
        newWeatherData[dateKey] = { temp: 0, temp_min: 0, temp_max: 0 };
      }
    }
    setWeatherData(newWeatherData);
  }, [weather]);

  // 📊 Extrae las temperaturas mínimas y máximas para el gráfico
  useEffect(() => {
    if (weatherData) {
      const newMinTemps = [];
      const newMaxTemps = [];
      Object.values(weatherData).forEach((data) => {
        newMinTemps.push(data.temp_min);
        newMaxTemps.push(data.temp_max);
      });
      setMinTemp(newMinTemps);
      setMaxTemp(newMaxTemps);
    }
  }, [weatherData]);

  // 🏷️ Genera etiquetas para el eje X con los días del mes
  const labels = days.map(
    (day) =>
      `${day.weekday.charAt(0).toUpperCase() + day.weekday.slice(1, 3)} ${day.day}`
  );

  // 📉 Configuración del dataset para el gráfico
  const data = {
    labels: labels,
    datasets: [
      {
        data: generalReport
          ? labels.map((label) => {
              const day = label.split(' ')[1];
              return generalReport[day]?.max
                ? parseFloat(generalReport[day]?.max)
                : 0;
            })
          : [],
        label: legend,
        fill: false,
        borderColor: '#909090',
        borderWidth: 4,
        lineTension: 0.3,
        yAxisID: 'y-axis-1',
      },
      {
        data: maxTemp,
        label: maxWeather,
        fill: false,
        borderColor: '#ED685E',
        borderWidth: 1,
        lineTension: 0.3,
        yAxisID: 'y-axis-2',
      },
      {
        data: minTemp,
        label: minWeather,
        fill: false,
        borderColor: '#00AFEF',
        borderWidth: 1,
        lineTension: 0.3,
        yAxisID: 'y-axis-2',
      },
    ],
  };

  // 📈 Configuración del gráfico
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: true },
    tooltips: {
      shared: true,
      callbacks: {
        label: function (tooltipItem, data) {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const value = dataset.data[tooltipItem.index];
          if (tooltipItem.datasetIndex === 0) {
            return `${value.toFixed(0)} kW`; // Demand dataset
          } else {
            return `${value.toFixed(2)}°C`; // Temperature datasets
          }
        },
      },
    },
    scales: {
      'y-axis-1': {
        display: true,
        position: 'right',
        ticks: {
          beginAtZero: true, // Esto asegura que el cero se dibuje desde abajo
          callback: function (value) {
            return `${value.toFixed(0)} kW`; // Aseguramos que los valores se muestren correctamente
          },
          suggestedMin: 0, // Asegura que el gráfico no empiece en valores negativos
          suggestedMax: Math.max(...data.datasets[0].data) * 1.1, // Ajuste automático para un máximo razonable
        },
      },
      'y-axis-2': {
        position: 'left',
        ticks: {
          beginAtZero: true, // Comienza en cero
          callback: function (value) {
            return `${value.toFixed(0)}°C`; // Asegura que los valores sean correctos
          },
          suggestedMin: 0, // Evita que aparezcan valores negativos
        },
        grid: {
          drawOnChartArea: false, // Elimina la grilla de este eje
        },
      },
    },
  };

  return (
    <div ref={chartRef}>
      <Portlet>
        <PortletHeader noDivider>
          <PortletLabel title={title} />
          <PortletToolbar style={{ display: 'flex', gap: 8 }}>
            <Download itemRef={chartRef} name={title} />
            <ShareButton itemRef={chartRef} name={title} />
          </PortletToolbar>
        </PortletHeader>
        <PortletContent>
          <Box height={400}>
            <Line data={data} options={options} />
          </Box>
        </PortletContent>
      </Portlet>
    </div>
  );
}
